/* TM Pet Store — cart-drawer.js
   AJAX add to cart, quantity updates and live cart counter via the
   Shopify Ajax Cart API + Section Rendering API. No dependencies. */
(() => {
  'use strict';

  const theme = (window.theme = window.theme || {});
  const SECTION_ID = 'cart-drawer';
  const DRAWER_ID = 'CartDrawer';
  const $ = (sel, ctx = document) => ctx.querySelector(sel);
  const $$ = (sel, ctx = document) => Array.from(ctx.querySelectorAll(sel));

  const drawerEl = () => document.getElementById(DRAWER_ID);
  const usesDrawer = () => theme.cartType !== 'page' && !!drawerEl();
  const openDrawer = () => theme.drawer && theme.drawer.open(DRAWER_ID);

  /* ---------- Rendering ---------- */
  function updateCount(count) {
    $$('[data-cart-count]').forEach((badge) => {
      badge.textContent = count;
      badge.hidden = count === 0;
      badge.classList.remove('is-bumped');
      void badge.offsetWidth; // restart animation
      if (count > 0) badge.classList.add('is-bumped');
    });
  }

  function renderDrawer(html) {
    if (!html) return false;
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const fresh = $('[data-cart-drawer-content]', doc);
    const current = $('[data-cart-drawer-content]');
    if (!fresh) return false;
    const hadFocus = current && current.contains(document.activeElement);
    if (current) current.replaceWith(fresh);
    updateCount(parseInt(fresh.dataset.cartCountValue, 10) || 0);
    if (hadFocus) {
      const panel = $(`#${DRAWER_ID} .drawer__panel`);
      if (panel) panel.focus({ preventScroll: true });
    }
    return true;
  }

  async function refresh() {
    try {
      const res = await fetch(`${theme.routes.root.replace(/\/$/, '')}/?section_id=${SECTION_ID}`);
      if (res.ok && renderDrawer(await res.text())) return;
      const cart = await (await fetch(`${theme.routes.cart}.js`)).json();
      updateCount(cart.item_count);
    } catch (err) {
      /* network hiccup — leave UI as is */
    }
  }

  const errorMessage = (data) =>
    (data && (typeof data.description === 'string' ? data.description : data.message)) || theme.strings.cartError;

  /* ---------- Add to cart ---------- */
  function setLoading(buttons, loading) {
    buttons.forEach((btn) => {
      btn.classList.toggle('is-loading', loading);
      btn.setAttribute('aria-busy', String(loading));
      if (loading) btn.dataset.wasDisabled = btn.disabled ? '1' : '';
      btn.disabled = loading || btn.dataset.wasDisabled === '1';
    });
  }

  function flashAdded(buttons) {
    buttons.forEach((btn) => {
      const label = $('[data-add-label]', btn);
      if (!label) return;
      const original = label.textContent;
      btn.classList.add('is-added');
      label.textContent = theme.strings.added;
      setTimeout(() => {
        btn.classList.remove('is-added');
        label.textContent = original;
      }, 1600);
    });
  }

  async function addToCart(form) {
    const buttons = [
      ...$$('[data-add-button]', form),
      ...(form.id ? $$(`[data-add-button][form="${form.id}"]`) : [])
    ];
    const errorEl = $('[data-form-error]', form);
    if (errorEl) errorEl.hidden = true;
    setLoading(buttons, true);

    const body = new FormData(form);
    if (usesDrawer()) {
      body.append('sections', SECTION_ID);
      body.append('sections_url', window.location.pathname);
    }

    try {
      const res = await fetch(theme.routes.cartAdd, {
        method: 'POST',
        headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/javascript' },
        body
      });
      const data = await res.json();
      if (!res.ok || data.status) throw data;

      if (!usesDrawer()) {
        window.location.href = theme.routes.cart;
        return;
      }
      if (!renderDrawer(data.sections && data.sections[SECTION_ID])) await refresh();
      flashAdded(buttons);
      openDrawer();
      document.dispatchEvent(new CustomEvent('cart:added', { detail: data }));
    } catch (data) {
      const message = errorMessage(data);
      if (errorEl) {
        errorEl.textContent = message;
        errorEl.hidden = false;
      }
      if (theme.toast) theme.toast(message, true);
    } finally {
      setLoading(buttons, false);
    }
  }

  document.addEventListener('submit', (e) => {
    const form = e.target.closest('form[data-product-form]');
    if (!form) return;
    e.preventDefault();
    addToCart(form);
  });

  /* ---------- Change quantity / remove ---------- */
  async function changeLine(line, quantity, row) {
    if (row) row.classList.add('is-updating');
    try {
      const res = await fetch(theme.routes.cartChange, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line, quantity, sections: [SECTION_ID], sections_url: window.location.pathname })
      });
      const data = await res.json();
      if (!res.ok || data.status) throw data;
      if (!renderDrawer(data.sections && data.sections[SECTION_ID])) await refresh();
    } catch (data) {
      if (row) row.classList.remove('is-updating');
      if (theme.toast) theme.toast(errorMessage(data), true);
      await refresh();
    }
  }

  document.addEventListener('click', (e) => {
    const qtyBtn = e.target.closest('[data-cart-qty]');
    if (qtyBtn && qtyBtn.closest('[data-cart-drawer-content]')) {
      e.preventDefault();
      changeLine(parseInt(qtyBtn.dataset.line, 10), Math.max(0, parseInt(qtyBtn.dataset.cartQty, 10)), qtyBtn.closest('.cart-item'));
      return;
    }
    const opener = e.target.closest('[data-cart-open]');
    if (opener && usesDrawer()) {
      e.preventDefault();
      theme.drawer.open(DRAWER_ID, opener);
    }
  });

  /* ---------- Order note ---------- */
  let noteTimer;
  document.addEventListener('input', (e) => {
    if (!e.target.matches('[data-cart-note]')) return;
    clearTimeout(noteTimer);
    noteTimer = setTimeout(() => {
      fetch(theme.routes.cartUpdate, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ note: e.target.value })
      }).catch(() => {});
    }, 500);
  });

  /* ---------- Keep counter fresh after back/forward navigation ---------- */
  window.addEventListener('pageshow', (e) => {
    if (e.persisted) refresh();
  });

  theme.cart = { open: openDrawer, refresh, add: addToCart };
})();
